import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import ProtectedRoute from '../components/auth/ProtectedRoute'
import { UserProfile, CurrentAgenda, AppHeader, CompetitionResultsCard } from '../components'
import { apiService } from '../services/api'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'

export default function DashboardPage() {
  const { user, updateProfile } = useAuth()
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)

  const handleUpdateProfile = async (updates: Partial<any>): Promise<boolean> => {
    setIsUpdatingProfile(true)
    try {
      // Use the updateProfile function from auth context if available
      if (updateProfile) {
        await updateProfile(updates)
        return true
      }
      // Fallback to API service
      const response = await apiService.updateUserProfile(updates)
      return !response.error
    } catch (error) {
      console.error('Profile update failed:', error)
      return false
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  // ProtectedRoute ensures user is not null, but TypeScript doesn't know that
  // So we add a safety check
  if (!user) {
    return null
  }

  return (
    <ProtectedRoute>
      <Helmet>
        <title>Dashboard - FWO Pilot Portal</title>
        <meta name="description" content="Your FlyWideOpen Pilot Portal dashboard" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Header */}
        <AppHeader activePage="dashboard" />

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome back, {user.firstName}!
            </h2>
            <p className="text-gray-600">
              Manage your profile and competition information
            </p>
          </div>

          {/* Agenda and Quick Actions — side-by-side on desktop, stacked on mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Current Agenda — first section */}
            <div>
              <CurrentAgenda />
            </div>

            {/* Quick Actions — beside agenda on desktop, below on mobile */}
            <div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-full">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Link
                    to="/events"
                    className="block w-full px-4 py-2 text-center bg-fwo-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    View All Events
                  </Link>
                  <Link
                    to="/photos"
                    className="block w-full px-4 py-2 text-center border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Photo Competition
                  </Link>
                  <Link
                    to="/membership"
                    className="block w-full px-4 py-2 text-center border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Pilot ID Card
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Competition Results — below agenda/quick actions */}
          <div className="mt-8">
            <CompetitionResultsCard />
          </div>

          {/* User Profile — full width, below Competition Results */}
          <div className="mt-8">
            <UserProfile 
              user={user}
              onUpdateProfile={handleUpdateProfile}
              isLoading={isUpdatingProfile}
            />
          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}